// ABOUTME: Renders metadata-driven single and multi-select autocomplete fields.
// ABOUTME: Keeps form state aligned with loaded option objects for persisted values.
import React from "react";
import Autocomplete, { AutocompleteProps } from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import {
    getControlOptionsAndChildrenFilterV2,
    resolveAutocompleteOptionValue
} from "./util/AutoCompleteUtil";
import { clearArrayInfo } from "functions/util";
import _ from "lodash";
import { getErrorAndMessages } from "./util/general";
import {
    IInputProps,
    useRhfFieldControlled
} from "../helpers/reactHookFormsHelper";
import { useWatch } from "react-hook-form";

export interface ISelectProps {
    options: any;
    customLists?: Compleo.IObject[];
    multiple?: boolean;
    parentFilterField?: Compleo.IObject;
    childrenFilterFieldName?: string;
    language?: string;
    groupByField?: string;
    disableClearable?: boolean;
    campoMetadata: Compleo.IObject;
    row?: boolean;
    freeSolo?: boolean;
    setValueToDefaultIfNotInList?: boolean;
    /**
     * Issue #249 — When provided, this message is displayed as a warning in the
     * field helper-text area if the saved value is not found in the loaded list,
     * instead of silently resetting the field to its default value.
     *
     * Example: "Assessment not found. Please select again."
     */
    notFoundMessage?: string;
    isHtml?: boolean;
    disabled?: boolean;
}

const ComboAutoComplete = (props: ISelectProps & IInputProps) => {
    const {
        name,
        multiple,
        label,
        options,
        helperTextDefault,
        campoMetadata,
        customLists,
        required,
        groupByField,
        language: languageProps,
        setValueToDefaultIfNotInList = true,
        notFoundMessage,
        ...other
    } = props;
    const language = languageProps || "pt-BR";
    const {
        error,
        message,
        setFieldValue,
        field,
        getValues,
        watch,
        setValue,
        control
    } = useRhfFieldControlled(name, helperTextDefault);

    const multipleControl = multiple === undefined ? false : multiple;

    let defaultValue: any = null;
    if (multipleControl) {
        defaultValue = [];
    }

    let controlValue = defaultValue;
    const valueFromFormik = watch(field.name);

    const fieldValue = useWatch({
        control,
        name: props?.parentFilterField?.parentFieldName
    });
    const controlOptionsAndChildrenFilter = getControlOptionsAndChildrenFilterV2(
        campoMetadata,
        options,
        customLists,
        name,
        fieldValue,
        props
    );
    const controlOptions = controlOptionsAndChildrenFilter.controlOptions;
    const childrenFilterFieldName =
        controlOptionsAndChildrenFilter.childrenFilterFieldName;

    const childrenFieldValue = useWatch({
        control,
        name: childrenFilterFieldName
    });

    if (
        valueFromFormik !== "" &&
        valueFromFormik !== undefined &&
        valueFromFormik !== null
    ) {
        controlValue = resolveAutocompleteOptionValue(
            valueFromFormik,
            controlOptions,
            language
        );
    }

    React.useEffect(() => {
        const resolvedValue = resolveAutocompleteOptionValue(
            valueFromFormik,
            controlOptions,
            language
        );

        if (resolvedValue !== valueFromFormik) {
            setValue(field.name, resolvedValue, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false
            });
        }
    }, [controlOptions, field.name, language, setValue, valueFromFormik]);

    // Issue #249 — Guard: for async custom lists, only trigger the
    // "value-not-in-list" logic once the list entry has been added to
    // customLists (i.e., the API has returned). This prevents a premature
    // reset that occurs while the list is still loading.
    const isCustomListLoaded = React.useMemo(() => {
        if (!campoMetadata.selectCustomList) {
            // Static options — always considered loaded
            return true;
        }
        // List is loaded when its entry is present in customLists
        return (
            (customLists || []).some(
                (c: Compleo.IObject) => c.fieldName === clearArrayInfo(name)
            )
        );
    }, [campoMetadata.selectCustomList, customLists, name]);

    // Issue #249 — Track whether the saved value is absent from the loaded list
    // so we can surface a warning to the user rather than silently zeroing it.
    const [valueNotFoundWarning, setValueNotFoundWarning] = React.useState(
        false
    );

    React.useEffect(() => {
        // Only run when the list is fully loaded (guard for async lists)
        if (!isCustomListLoaded) {
            return;
        }

        if (
            valueFromFormik &&
            (controlOptions || []).length > 0 &&
            !(controlOptions || []).find(
                (item: any) => item?.value === valueFromFormik.value
            )
        ) {
            if (notFoundMessage) {
                // Show warning instead of silently resetting (Issue #249)
                setValueNotFoundWarning(true);
            } else if (setValueToDefaultIfNotInList) {
                setFieldValue(defaultValue);
                setValueNotFoundWarning(false);
            }
        } else {
            setValueNotFoundWarning(false);
        }
    }, [valueFromFormik, controlOptions, isCustomListLoaded]);

    // Displayed helper text: warning takes priority when active
    const helperTextFinal = valueNotFoundWarning
        ? notFoundMessage
        : message;

    return (
        <Autocomplete
            disableClearable={other.disableClearable}
            disabled={other.disabled}
            className={other.className}
            autoComplete
            multiple={multipleControl}
            id={name}
            value={controlValue}
            options={controlOptions}
            groupBy={
                groupByField ? (option) => option[groupByField] : undefined
            }
            getOptionLabel={(option: any) => {
                return option.label || option[`label-${language}`] || "";
            }}
            getOptionSelected={(option, value) => {
                return value.value === option.value;
            }}
            // inputValue={(controlValue || {}).label || ""}
            onChange={(event: any, newValue: any | null) => {
                if (
                    newValue === null ||
                    newValue === undefined ||
                    newValue === ""
                ) {
                    setFieldValue(defaultValue);
                } else {
                    setFieldValue(newValue);
                }
                if (
                    childrenFilterFieldName !== undefined &&
                    childrenFilterFieldName !== ""
                ) {
                    if (childrenFilterFieldName.includes(",")) {
                        childrenFilterFieldName
                            .split(",")
                            .map((item: string) => {
                                if (Array.isArray(childrenFieldValue)) {
                                    setValue(item, []);
                                } else {
                                    setValue(item, defaultValue);
                                }
                            });
                    } else {
                        if (Array.isArray(childrenFieldValue)) {
                            setValue(childrenFilterFieldName, []);
                        } else {
                            setValue(childrenFilterFieldName, defaultValue);
                        }
                    }
                }
            }}
            onBlur={field.onBlur}
            renderInput={(params: any) => (
                <TextField
                    {...(other?.inputProps || {})}
                    {...params}
                    error={error || valueNotFoundWarning}
                    required={required}
                    label={label}
                    // margin="normal"
                    helperText={helperTextFinal}
                    autoComplete="new-password"
                    data-testid={field.name}
                    // {...field}
                    inputRef={field.ref}
                />
            )}
        />
    );
};

export default ComboAutoComplete;
